/**
 * ProTierGate — shared upgrade prompt for Tier-1 Pro tools.
 *
 * If the current user is not `pro`/`admin`/`moderator`, we render a
 * frosted-glass CTA over the tool's normal UI (children remain mounted
 * so their static preview is still visible under the blur, giving
 * curious free users a taste of what they'll unlock).
 *
 * Usage:
 *   <ProTierGate toolName="Recipe Builder">
 *     <RecipeBuilderInner />
 *   </ProTierGate>
 */
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';

const PRO_ROLES = new Set(['pro', 'admin', 'moderator']);

export const useIsPro = () => {
  const { user } = useContext(AuthContext);
  return PRO_ROLES.has(user?.role);
};

const ProTierGate = ({ toolName, tagline, children, data_testid_prefix = 'pro-gate' }) => {
  const isPro = useIsPro();
  const navigate = useNavigate();

  if (isPro) return children;

  return (
    <div className="relative" data-testid={`${data_testid_prefix}-wrapper`}>
      {/* Faded preview of the tool */}
      <div className="pointer-events-none select-none opacity-30 blur-[2px]">
        {children}
      </div>

      {/* CTA overlay */}
      <div
        className="absolute inset-0 z-[1100] flex items-center justify-center p-6"
        data-testid={`${data_testid_prefix}-overlay`}
      >
        <div className="max-w-md w-full rounded-2xl bg-primary text-white shadow-2xl p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-white/15 flex items-center justify-center">
            <Lock className="w-6 h-6" />
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-widest font-extrabold text-white/70">
            Pro Tier · Business tools
          </div>
          <h2 className="mt-1 font-heading text-2xl font-extrabold">
            {toolName} is a Pro tool
          </h2>
          <p className="mt-2 text-sm text-slate-100">
            {tagline || 'Upgrade to Pro to unlock AI-powered analysis, cycle modelling, and production planning built for small-batch operators.'}
          </p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <Button
              onClick={() => navigate('/portal')}
              className="rounded-full bg-white text-primary hover:bg-slate-100 font-extrabold"
              data-testid={`${data_testid_prefix}-upgrade-btn`}
            >
              <Sparkles className="w-4 h-4 mr-1" /> Upgrade to Pro
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
              data-testid={`${data_testid_prefix}-back-btn`}
            >
              Not now <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProTierGate;
